// Pantalla de configuración — Reglas de sincronización masiva (por tenant, Req. 10.12.a).
// Solo accesible por propietarios (user_admin). Umbral de auto-aplicación, umbral
// de revisión e identificación fuerte (catálogo + artista) que auto-aplica aunque
// el puntaje sea menor.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../api/ApiContext.js';
import { AlertMessage, Card, Icon, PageHeader, Spinner } from '../../ui/components.js';
import { useAsync } from '../../ui/useAsync.js';

export function SyncRulesView() {
  const { t } = useTranslation();
  const api = useApi();
  const rules = useAsync(() => api.config.getSyncRules(), []);
  const [autoApply, setAutoApply] = useState<number | null>(null);
  const [review, setReview] = useState<number | null>(null);
  const [strongId, setStrongId] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const autoVal = autoApply ?? rules.data?.auto_apply_threshold ?? 95;
  const reviewVal = review ?? rules.data?.review_threshold ?? 70;
  const strongVal = strongId ?? rules.data?.auto_apply_strong_id ?? true;

  const invalid = autoVal < 0 || autoVal > 100 || reviewVal < 0 || reviewVal > 100 || autoVal < reviewVal;

  async function save() {
    if (invalid) {
      setError(t('syncRules.errRange'));
      return;
    }
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await api.config.setSyncRules({
        auto_apply_threshold: autoVal,
        review_threshold: reviewVal,
        auto_apply_strong_id: strongVal,
      });
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('syncRules.errSave'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col">
      <PageHeader
        title={t('syncRules.pageTitle')}
        subtitle={t('syncRules.pageSubtitle')}
        back
      />
      <div className="flex flex-col px-margin-mobile py-space-md gap-space-md">
        <Card title={t('syncRules.cardTitle')} icon="rule">
          <p className="font-body-sm text-body-sm text-on-surface-variant mb-space-md">
            {t('syncRules.cardHint')}
          </p>

          {rules.loading && <Spinner label={t('syncRules.loading')} />}
          {rules.error && <p className="text-error font-body-sm text-body-sm">{String(rules.error)}</p>}

          {!rules.loading && (
            <div className="flex flex-col gap-space-md">
              <ThresholdField
                label={t('syncRules.autoApplyLabel')}
                hint={t('syncRules.autoApplyHint')}
                value={autoVal}
                onChange={(v) => {
                  setSaved(false);
                  setAutoApply(v);
                }}
              />
              <ThresholdField
                label={t('syncRules.reviewLabel')}
                hint={t('syncRules.reviewHint')}
                value={reviewVal}
                onChange={(v) => {
                  setSaved(false);
                  setReview(v);
                }}
              />
              <label className="flex items-start gap-space-sm cursor-pointer">
                <input
                  type="checkbox"
                  className="accent-primary w-4 h-4 mt-1"
                  checked={strongVal}
                  onChange={(e) => {
                    setSaved(false);
                    setStrongId(e.target.checked);
                  }}
                />
                <span className="flex flex-col">
                  <span className="font-body-sm text-body-sm text-on-surface">
                    {t('syncRules.strongIdLabel')}
                  </span>
                  <span className="font-body-sm text-body-sm text-on-surface-variant">
                    {t('syncRules.strongIdHint')}
                  </span>
                </span>
              </label>

              {invalid && (
                <p className="text-error font-body-sm text-body-sm">
                  {t('syncRules.invalid')}
                </p>
              )}
              <AlertMessage message={error} />
              {saved && <p className="text-goldmine-mint font-body-sm text-body-sm">{t('syncRules.saved')}</p>}

              <button
                className="btn-primary w-full"
                type="button"
                disabled={saving || invalid}
                onClick={() => void save()}
              >
                <Icon name="save" className="text-[18px]" />
                {saving ? t('syncRules.saving') : t('syncRules.save')}
              </button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

/** Campo de umbral 0–100 con deslizador + entrada numérica. */
function ThresholdField({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="flex flex-col gap-space-xs">
      <div className="flex items-center justify-between">
        <label className="field-label">{label}</label>
        <span className="font-code-catalog text-body-sm text-primary">{value}%</span>
      </div>
      <div className="flex items-center gap-space-sm">
        <input
          type="range"
          min={0}
          max={100}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="flex-1 accent-primary"
        />
        <input
          type="number"
          min={0}
          max={100}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="field-input w-16 bg-surface-deck rounded border border-border-subtle px-2 h-9 text-center"
        />
      </div>
      <span className="font-body-sm text-body-sm text-on-surface-variant">{hint}</span>
    </div>
  );
}
